import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Calendar,
  CalendarCheck,
  ClipboardList,
  Languages,
  Users,
  UserCog,
  User,
  Bot,
  GraduationCap,
  Building,
  Sliders,
  X,
  LogOut,
  FileUp,
  Bug,
  Command,
  Award,
  TrendingUp,
  BarChart2,
  CreditCard,
  BookOpen,
  Coins,
  Rocket,
  DollarSign,
  Sun,
  ShieldAlert,
  Video,
  Gift,
  UsersRound, MessagesSquare, Gauge, ScrollText, LifeBuoy, Inbox, Mail, Lightbulb, MessageSquareText,
} from "lucide-react";
import useFeatures from "../hooks/useFeatures";
import { useEffect, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useTheme } from "../context/ThemeContext";
import { MindConnectMark, MindConnectWordmark, MindConnectTagline } from "../components/ui/MindConnectLogo";
import { allMenuItems as accessControlMenuItems } from "../config/navMenuItems";
import WhatsAppIcon from "../components/ui/WhatsAppIcon";

export default function Sidebar({ isOpen, onClose, collapsed: pinnedCollapsed = false, onToggleCollapse, topOffset = 0 }) {
  // While the desktop sidebar is collapsed (e.g. on the consultation pages,
  // to give the note more room), hovering it opens it over the page and
  // moving away folds it again; the page itself doesn't shift. A short delay
  // stops it flicking open when the mouse just passes over.
  const [peek, setPeek] = useState(false);
  const peekTimer = useRef(null);
  const collapsed = pinnedCollapsed && !peek;
  const startPeek = () => {
    if (!pinnedCollapsed) return;
    clearTimeout(peekTimer.current);
    peekTimer.current = setTimeout(() => setPeek(true), 150);
  };
  const endPeek = () => {
    clearTimeout(peekTimer.current);
    setPeek(false);
  };
  useEffect(() => () => clearTimeout(peekTimer.current), []);
  useEffect(() => { if (!pinnedCollapsed) setPeek(false); }, [pinnedCollapsed]);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme, themes } = useTheme();
  const [userRole, setUserRole] = useState(null);
  const [userOrgUid, setUserOrgUid] = useState(null);
  const [showNavigationConfirm, setShowNavigationConfirm] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState(null);
  const [appVersion, setAppVersion] = useState(() => {
    // Get version from environment variable first
    if (process.env.REACT_APP_VERSION) {
      return process.env.REACT_APP_VERSION;
    }
    // Fallback to "V unknown" - will try to fetch from version.txt if available
    return "V unknown";
  });

  useEffect(() => {
    // If environment variable is not set, try to fetch from version.txt (for local development)
    if (!process.env.REACT_APP_VERSION && appVersion === "V unknown") {
      fetch("/version.txt")
        .then((res) => (res.ok ? res.text() : ""))
        .then((text) => {
          if (text.trim()) {
            setAppVersion(text.trim());
          }
        })
        .catch(() => {
          // Keep "V unknown" if fetch fails
        });
    }
  }, [appVersion]);

  useEffect(() => {
    // Get user role from JWT token
    const storedUser = localStorage.getItem("auth");
    if (storedUser) {
      try {
        const authData = JSON.parse(storedUser);
        if (authData.token) {
          const decoded = jwtDecode(authData.token);
          // Role is stored in decoded.sub.role based on backend JWT structure
          const role = decoded.sub?.role || authData.role;
          setUserRole(role?.toLowerCase());
          setUserOrgUid(decoded.sub?.org_uid || null);
        } else if (authData.role) {
          // Fallback to role in auth data
          setUserRole(authData.role.toLowerCase());
        }
      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
  }, []);

  // Icons stay defined here (per nav entry, by name) since they're purely
  // a sidebar display concern - the actual access-control fields (path/
  // roles/adminOrgOnly) are imported from config/navMenuItems.js, the same
  // array ProtectedRoute.js uses to guard the routes themselves, so the
  // two can never drift out of sync the way they used to (this array used
  // to be the only copy, and only ever controlled whether the link
  // *showed* - the page underneath was always reachable by URL regardless
  // of role).
  const iconsByName = {
    "Home": <LayoutDashboard size={18} />,
    "Today": <Sun size={18} />,
    "Risk register": <ShieldAlert size={18} />,
    "Toolkit": <BookOpen size={18} />,
    "Supervision": <MessagesSquare size={18} />,
    "Team caseload": <UsersRound size={18} />,
    "Clinical oversight": <Gauge size={18} />,
    "Audit log": <ScrollText size={18} />,
    "Questionnaires & licences": <ClipboardList size={18} />,
    "Questionnaire translations": <Languages size={18} />,
    "Toolkit translations": <Languages size={18} />,
    "Getting Started": <Rocket size={18} />,
    "Help": <LifeBuoy size={18} />,
    "Team requests": <Inbox size={18} />,
    "SMS Templates": <MessageSquareText size={18} />,
    "WhatsApp Templates": <WhatsAppIcon size={18} />,
    "Website Enquiries": <Mail size={18} />,
    "Feature Requests": <Lightbulb size={18} />,
    "Finance": <DollarSign size={18} />,
    "Appointments": <CalendarCheck size={18} />,
    "Consultations": <ClipboardList size={18} />,
    "Calendar": <Calendar size={18} />,
    "Patients": <Users size={18} />,
    "Clinicians": <UserCog size={18} />,
    "Users": <User size={18} />,
    "Team Overview": <LayoutDashboard size={18} />,
    "My Team": <Users size={18} />,
    "Supervisors": <UserCog size={18} />,
    "Error Logs": <Bug size={18} />,
    "Org Credits": <CreditCard size={18} />,
    "Organisations": <Building size={18} />,
    "Consultations Overview": <ClipboardList size={18} />,
    "Org Payouts": <Coins size={18} />,
    "Signal KB": <BookOpen size={18} />,
    "Video Call": <Video size={18} />,
    "Free-Trial Campaign": <Gift size={18} />,
  };
  const features = useFeatures();
  const allMenuItems = accessControlMenuItems.map(item => ({ ...item, icon: iconsByName[item.name] }));

  // Filter menu items based on role, and for adminOrgOnly items also verify org_uid
  const adminOrgId = process.env.REACT_APP_ADMIN_ORG_ID;
  const menu = userRole
    ? allMenuItems.filter(item => {
      if (!item.roles.includes(userRole)) return false;
      if (item.adminOrgOnly && userOrgUid !== adminOrgId) return false;
      if (item.hideForAdminOrg && adminOrgId && userOrgUid === adminOrgId) return false;
      if (item.feature && !features.has(item.feature)) return false;
      if (item.hideWhenFeature && item.hideWhenFeature.roles.includes(userRole) && features.has(item.hideWhenFeature.feature)) return false;
      // Team pages: a Supervisor login, or anyone who has supervisees mapped
      if (item.needsSupervisees && userRole !== "supervisor" && !features.supervises) return false;
      return true;
    })
    : allMenuItems;

  const handleLinkClick = (e, targetPath) => {
    // If we're on /chat page, show confirmation dialog
    if (location.pathname === '/chat') {
      e.preventDefault();
      setPendingNavigationPath(targetPath);
      setShowNavigationConfirm(true);
      return;
    }

    // Close sidebar on mobile when a link is clicked
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  const handleConfirmNavigation = () => {
    setShowNavigationConfirm(false);
    if (pendingNavigationPath) {
      // Dispatch event to ChatSession to disable navigation blocking
      window.dispatchEvent(new CustomEvent('allowNavigation'));
      // Navigate after a brief delay to ensure ChatSession receives the event
      setTimeout(() => {
        navigate(pendingNavigationPath);
        setPendingNavigationPath(null);
      }, 100);
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationConfirm(false);
    setPendingNavigationPath(null);
  };

  // Toggling profile menu
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.user-profile-trigger')) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <>
      {/* Desktop Sidebar - Glassmorphism. `top`/`height` are inline (not the
          `top-0 h-full` Tailwind classes) so they can shift down when the
          subscription-expiry banner above is showing - `position: fixed`
          means this never participates in normal document flow, so being a
          sibling after the banner in the DOM doesn't push it down on its
          own; it has to be told the offset explicitly. */}
      <div
        className={`hidden md:flex flex-col fixed left-0 z-30 transition-all duration-300 backdrop-blur-xl border-r border-white/20 ${collapsed ? 'w-16' : 'w-64'} ${peek ? 'bg-white/95 shadow-2xl' : 'bg-white/70 shadow-glass'}`}
        style={{ top: topOffset, height: `calc(100vh - ${topOffset}px)` }}
        onMouseEnter={startPeek}
        onMouseLeave={endPeek}
      >
        {/* Header: logo + collapse toggle */}
        <div className={`py-4 font-bold text-2xl text-primary-600 flex items-center font-heading flex-shrink-0 ${collapsed ? 'justify-center px-0' : 'justify-between px-4'}`}>
          {!collapsed && (
            <div className="flex items-center gap-3">
              <MindConnectMark className="w-9 h-9 hover:scale-105 transition-transform" />
              <div className="leading-tight">
                <MindConnectWordmark className="text-xl" />
                <MindConnectTagline className="text-[9px]" />
              </div>
            </div>
          )}
          {collapsed && (
            <MindConnectMark className="w-8 h-8" />
          )}
          <button
            onClick={() => { endPeek(); onToggleCollapse(); }}
            className="p-1.5 rounded-lg text-secondary-400 hover:bg-primary-50 hover:text-primary-600 transition-all"
            title={pinnedCollapsed ? (peek ? 'Keep sidebar open' : 'Expand sidebar') : 'Collapse sidebar'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 transition-transform duration-300 ${pinnedCollapsed ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
          </button>
        </div>

        {/* Nav with scroll */}
        <nav className="flex-1 px-2 py-1 space-y-0.5 overflow-y-auto custom-scrollbar">
          {menu.map((item, idx) => {
            const isActive = location.pathname === item.path;
            let dataTourId = null;
            if (item.path === '/dashboard') dataTourId = 'dashboard';
            else if (item.path === '/patient-management') dataTourId = 'patients';
            else if (item.path === '/doctor-management') dataTourId = 'doctors';
            else if (item.path === '/appointments') dataTourId = 'appointments';
            const newGroup = item.group && item.group !== menu[idx - 1]?.group;
            const heading = newGroup && (collapsed
              ? (idx > 0 && <div key={`g-${item.group}`} className="mx-3 my-2 border-t border-secondary-200/70" aria-hidden="true" />)
              : <p key={`g-${item.group}`} className={`px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-secondary-400 ${idx > 0 ? "pt-3" : "pt-1"}`}>{item.group}</p>);

            return [heading, collapsed ? (
              <Link
                key={item.name}
                to={item.path}
                data-label={item.name}
                data-tour={dataTourId}
                onClick={(e) => handleLinkClick(e, item.path)}
                title={item.name}
                className={`flex items-center justify-center py-2 rounded-xl transition-all duration-200 group ${isActive
                  ? "bg-primary-500 text-white shadow-lg shadow-primary-500/25"
                  : "text-secondary-500 hover:bg-white/50 hover:text-primary-600"
                }`}
              >
                <span className={`${isActive ? "text-white" : "text-secondary-400 group-hover:text-primary-500"}`}>{item.icon}</span>
              </Link>
            ) : (
              <Link
                key={item.name}
                to={item.path}
                data-label={item.name}
                data-tour={dataTourId}
                onClick={(e) => handleLinkClick(e, item.path)}
                className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-all duration-200 group flex-shrink-0 ${isActive
                  ? "bg-primary-500 text-white shadow-md shadow-primary-500/25 font-medium"
                  : "text-secondary-500 hover:bg-white/50 hover:text-primary-600 hover:shadow-soft"
                }`}
              >
                <span className={`${isActive ? "text-white" : "text-secondary-400 group-hover:text-primary-500"}`}>{item.icon}</span>
                <span>{item.name}</span>
              </Link>
            )];
          })}
        </nav>

        {/* User Profile Section (Pinned Bottom) */}
        <div className={`py-4 mt-auto border-t border-secondary-200/50 bg-white/30 backdrop-blur-sm flex-shrink-0 relative ${collapsed ? 'px-2' : 'px-4'}`}>
          <div
            className={`user-profile-trigger flex items-center p-2 rounded-xl transition-all cursor-pointer select-none ${collapsed ? 'justify-center' : 'gap-3'} ${isProfileOpen ? 'bg-white shadow-sm ring-1 ring-black/5' : 'hover:bg-white/50'}`}
            title={collapsed ? 'Profile' : ''}
            onClick={(e) => {
              e.stopPropagation();
              setIsProfileOpen(!isProfileOpen);
            }}
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 text-white flex items-center justify-center font-bold shadow-sm ring-2 ring-white flex-shrink-0">
              {(() => {
                try {
                  const storedUser = localStorage.getItem("auth");
                  if (storedUser) {
                    const auth = JSON.parse(storedUser);
                    const name = auth.user_name || auth.name;
                    if (name) return name.charAt(0).toUpperCase();
                  }
                  return "U";
                } catch (e) { return "U"; }
              })()}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-secondary-800 truncate">
                  {(() => {
                    try {
                      const storedUser = localStorage.getItem("auth");
                      if (storedUser) return JSON.parse(storedUser).user_name || JSON.parse(storedUser).name || "User";
                      return "User";
                    } catch (e) { return "User"; }
                  })()}
                </p>
                <p className="text-xs text-secondary-500 truncate capitalize">{userRole || 'User'}</p>
              </div>
            )}
          </div>

          {/* Click-based Menu (Brand Theme for Distinction) */}
          {isProfileOpen && (
            // Collapsed sidebar is only w-16, so the menu gets its own width
            // there (spilling past the sidebar edge) instead of being squeezed.
            <div className={`absolute bottom-full mb-2 bg-gradient-to-br from-primary-600 to-primary-700 text-white rounded-xl shadow-xl shadow-primary-900/20 overflow-hidden p-1 z-50 animate-in slide-in-from-bottom-2 fade-in duration-200 ring-1 ring-white/20 ${collapsed ? 'left-2 w-56' : 'left-4 right-4'}`}>
              <button onClick={() => navigate("/profile")} className="w-full text-left px-3 py-2.5 text-xs font-medium text-white hover:bg-white/10 rounded-lg flex items-center gap-2 transition-colors">
                <UserCog size={14} className="text-primary-100" /> Profile
              </button>
              <button onClick={() => navigate("/settings")} className="w-full text-left px-3 py-2.5 text-xs font-medium text-white hover:bg-white/10 rounded-lg flex items-center gap-2 transition-colors">
                <Sliders size={14} className="text-primary-100" /> Settings
              </button>

              <div className="px-3 py-2">
                <p className="text-[10px] uppercase font-bold text-white/60 mb-2">Theme</p>
                <div className="flex gap-2">
                  {themes.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTheme(t.id)}
                      className={`w-6 h-6 rounded-full border-2 transition-all ${theme === t.id ? 'border-white scale-110 shadow-lg' : 'border-transparent hover:scale-105'}`}
                      style={{ backgroundColor: t.color }}
                      title={t.name}
                    />
                  ))}
                </div>
              </div>

              <div className="h-px bg-white/20 my-1"></div>

              {/* Keyboard Shortcuts Hint */}
              <button
                onClick={() => {
                  // Trigger the keyboard shortcut modal
                  const event = new KeyboardEvent('keydown', {
                    key: '/',
                    ctrlKey: !navigator.platform.toUpperCase().includes('MAC'),
                    metaKey: navigator.platform.toUpperCase().includes('MAC'),
                    bubbles: true
                  });
                  window.dispatchEvent(event);
                }}
                className="w-full text-left px-3 py-2.5 text-xs font-medium text-white hover:bg-white/10 rounded-lg flex items-center gap-2 transition-colors group"
              >
                <Command size={14} className="text-primary-100" />
                <span className="flex-1">Keyboard Shortcuts</span>
                <kbd className="px-1.5 py-0.5 text-[10px] bg-white/10 border border-white/20 rounded group-hover:bg-white/20 transition-colors">
                  {navigator.platform.toUpperCase().includes('MAC') ? '⌘' : 'Ctrl'} /
                </kbd>
              </button>

              <div className="h-px bg-white/20 my-1"></div>
              <button
                onClick={() => {
                  localStorage.removeItem("auth");
                  localStorage.removeItem("chatState");
                  localStorage.removeItem("patient_type");
                  localStorage.removeItem("username");
                  localStorage.removeItem("token");
                  navigate("/");
                }}
                className="w-full text-left px-3 py-2.5 text-xs font-bold text-rose-200 hover:bg-rose-500/20 hover:text-white rounded-lg flex items-center gap-2 transition-colors"
              >
                <LogOut size={14} /> Logout
              </button>
            </div>
          )}

          <div className="flex justify-between items-center mt-3 text-[10px] text-secondary-400 px-2 opacity-60 hover:opacity-100 transition-opacity">
            <span>{appVersion}</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar - Slide in with Glass effect */}
      < div
        className={`md:hidden fixed left-0 w-72 bg-white/95 backdrop-blur-xl text-secondary-800 flex flex-col z-50 transform transition-transform duration-300 ease-in-out shadow-2xl ${isOpen ? "translate-x-0" : "-translate-x-full"
          }`
        }
        style={{ top: topOffset, height: `calc(100vh - ${topOffset}px)` }}
      >
        {/* Mobile Header */}
        < div className="px-6 py-5 font-bold text-xl border-b border-secondary-100 flex items-center justify-between" >
          <div className="flex items-center gap-3 text-primary-600 font-heading">
            <MindConnectMark className="w-9 h-9" />
            <div className="leading-tight">
              <MindConnectWordmark className="text-xl" />
              <MindConnectTagline className="text-[9px]" />
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-secondary-400 hover:bg-secondary-50 transition-colors"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div >
        <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
          {menu.map((item, idx) => {
            const isActive = location.pathname === item.path;
            const heading = item.group && item.group !== menu[idx - 1]?.group
              ? <p key={`g-${item.group}`} className={`px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-secondary-400 ${idx > 0 ? "pt-3" : ""}`}>{item.group}</p>
              : null;

            let dataTourId = null;
            if (item.path === '/dashboard') dataTourId = 'dashboard';
            else if (item.path === '/patient-management') dataTourId = 'patients';
            else if (item.path === '/doctor-management') dataTourId = 'doctors';
            else if (item.path === '/appointments') dataTourId = 'appointments';

            return [heading, (
              <Link
                key={item.name}
                to={item.path}
                data-label={item.name}
                data-tour={dataTourId}
                onClick={(e) => handleLinkClick(e, item.path)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${isActive
                  ? "bg-primary-500 text-white shadow-lg shadow-primary-500/25"
                  : "text-secondary-500 hover:bg-secondary-50"
                  }`}
              >
                <span className={`${isActive ? "text-white" : "text-secondary-400"}`}>
                  {item.icon}
                </span>
                <span>{item.name}</span>
              </Link>
            )];
          })}
        </nav>
        {/* User Profile Section (Pinned Bottom) */}
        <div className="px-4 py-4 mt-auto border-t border-secondary-200/50 bg-white/30 backdrop-blur-sm flex-shrink-0 relative">
          <div
            className={`user-profile-trigger flex items-center gap-3 p-2 rounded-xl transition-all cursor-pointer select-none ${isProfileOpen ? 'bg-white shadow-sm ring-1 ring-black/5' : 'hover:bg-white/50'}`}
            onClick={(e) => {
              e.stopPropagation();
              setIsProfileOpen(!isProfileOpen);
            }}
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 text-white flex items-center justify-center font-bold shadow-sm ring-2 ring-white">
              {(() => {
                try {
                  const storedUser = localStorage.getItem("auth");
                  if (storedUser) {
                    const auth = JSON.parse(storedUser);
                    const name = auth.user_name || auth.name;
                    if (name) return name.charAt(0).toUpperCase();
                  }
                  return "U";
                } catch (e) { return "U"; }
              })()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-secondary-800 truncate">
                {(() => {
                  try {
                    const storedUser = localStorage.getItem("auth");
                    if (storedUser) return JSON.parse(storedUser).user_name || JSON.parse(storedUser).name || "User";
                    return "User";
                  } catch (e) { return "User"; }
                })()}
              </p>
              <p className="text-xs text-secondary-500 truncate capitalize">
                {userRole || 'User'}
              </p>
            </div>
          </div>

          {/* Click-based Menu (Brand Theme for Distinction) */}
          {isProfileOpen && (
            <div className="absolute bottom-full left-4 right-4 mb-2 bg-gradient-to-br from-primary-600 to-primary-700 text-white rounded-xl shadow-xl shadow-primary-900/20 overflow-hidden p-1 z-50 animate-in slide-in-from-bottom-2 fade-in duration-200 ring-1 ring-white/20">
              <button onClick={() => { navigate("/profile"); onClose(); }} className="w-full text-left px-3 py-2.5 text-xs font-medium text-white hover:bg-white/10 rounded-lg flex items-center gap-2 transition-colors">
                <UserCog size={14} className="text-primary-100" /> Profile
              </button>
              <button onClick={() => { navigate("/settings"); onClose(); }} className="w-full text-left px-3 py-2.5 text-xs font-medium text-white hover:bg-white/10 rounded-lg flex items-center gap-2 transition-colors">
                <Sliders size={14} className="text-primary-100" /> Settings
              </button>

              <div className="px-3 py-2">
                <p className="text-[10px] uppercase font-bold text-white/60 mb-2">Theme</p>
                <div className="flex gap-2">
                  {themes.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTheme(t.id)}
                      className={`w-6 h-6 rounded-full border-2 transition-all ${theme === t.id ? 'border-white scale-110 shadow-lg' : 'border-transparent hover:scale-105'}`}
                      style={{ backgroundColor: t.color }}
                      title={t.name}
                    />
                  ))}
                </div>
              </div>

              <div className="h-px bg-white/20 my-1"></div>

              {/* Keyboard Shortcuts Hint */}
              <button
                onClick={() => {
                  onClose();
                  // Trigger the keyboard shortcut modal
                  const event = new KeyboardEvent('keydown', {
                    key: '/',
                    ctrlKey: !navigator.platform.toUpperCase().includes('MAC'),
                    metaKey: navigator.platform.toUpperCase().includes('MAC'),
                    bubbles: true
                  });
                  window.dispatchEvent(event);
                }}
                className="w-full text-left px-3 py-2.5 text-xs font-medium text-white hover:bg-white/10 rounded-lg flex items-center gap-2 transition-colors group"
              >
                <Command size={14} className="text-primary-100" />
                <span className="flex-1">Keyboard Shortcuts</span>
                <kbd className="px-1.5 py-0.5 text-[10px] bg-white/10 border border-white/20 rounded group-hover:bg-white/20 transition-colors">
                  {navigator.platform.toUpperCase().includes('MAC') ? '⌘' : 'Ctrl'} /
                </kbd>
              </button>

              <div className="h-px bg-white/20 my-1"></div>
              <button
                onClick={() => {
                  onClose();
                  localStorage.removeItem("auth");
                  localStorage.removeItem("chatState");
                  localStorage.removeItem("patient_type");
                  localStorage.removeItem("username");
                  localStorage.removeItem("token");
                  navigate("/");
                }}
                className="w-full text-left px-3 py-2.5 text-xs font-bold text-rose-200 hover:bg-rose-500/20 hover:text-white rounded-lg flex items-center gap-2 transition-colors"
              >
                <LogOut size={14} /> Logout
              </button>
            </div>
          )}

          <div className="flex justify-between items-center mt-3 text-[10px] text-secondary-400 px-2 opacity-60 hover:opacity-100 transition-opacity">
            <span>Version: {appVersion}</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
          </div>
        </div>
      </div >

      {/* Navigation Confirmation Dialog */}
      {
        showNavigationConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-xl font-bold mb-4 text-gray-800">Leave Chat Session?</h3>
              <p className="text-gray-600 mb-6">
                You have been charged credits to initiate this conversation with the patient.
                Leaving or refreshing this page will result in the loss of your charged credits.
                Are you sure you want to proceed?
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCancelNavigation}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                  data-label="Cancel Navigation"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmNavigation}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                  data-label="Confirm Leave Session"
                >
                  Leave Session
                </button>
              </div>
            </div>
          </div>
        )
      }
    </>
  );
}
